import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * ComboBox Component
 *
 * Searchable dropdown that also allows free-text input.
 * User can pick from suggestions or type a custom value.
 * Based on Preline UI Advanced Select patterns.
 *
 * Options can be plain strings or {value, label} objects.
 * The dropdown renders via a portal so it floats above any
 * parent overflow constraints (e.g. modals with overflow:auto).
 *
 * @param {Object} props
 * @param {string} [props.label] - Label text
 * @param {string} props.value - Current value (controlled)
 * @param {Function} props.onChange - Callback with the new string value
 * @param {Array<string|{value:string, label:string}>} [props.options] - Suggestions
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.required] - Show required indicator
 * @param {boolean} [props.disabled] - Disable the input
 */
export function ComboBox({
  label,
  value = '',
  onChange,
  options = [],
  placeholder,
  error,
  required = false,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Normalize options to {value, label}
  const normalized = useMemo(() =>
    options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt)),
    [options],
  );

  // Filter options based on current value
  const filtered = useMemo(() => {
    if (!value) return normalized;
    const lower = value.toLowerCase();
    return normalized.filter(
      (opt) => opt.label.toLowerCase().includes(lower) || opt.value.toLowerCase().includes(lower),
    );
  }, [normalized, value]);

  // Reposition the portal dropdown under the input
  const updatePosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Close on outside click (portal-aware)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reposition on open & on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightIndex(-1);
  }, [filtered.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex];
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const selectOption = (opt) => {
    onChange(opt.value);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && filtered[highlightIndex]) {
          selectOption(filtered[highlightIndex]);
        } else {
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  // Show translated label if value matches a known option
  const displayValue = useMemo(() => {
    const match = normalized.find((opt) => opt.value === value);
    return match ? match.label : value;
  }, [normalized, value]);

  const inputClasses = `
    py-3 ps-4 pe-9 block w-full
    border-border-default rounded-lg text-sm
    focus:border-brand focus:ring-brand
    disabled:opacity-50 disabled:pointer-events-none
    ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className="block text-xs font-medium text-muted mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          value={displayValue}
          onChange={(e) => {
            // When user types, check if input matches a known label → store value
            const typed = e.target.value;
            const match = normalized.find((opt) => opt.label === typed);
            onChange(match ? match.value : typed);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClasses}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          className="absolute top-1/2 end-2.5 -translate-y-1/2 cursor-pointer"
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
        >
          <Icon
            icon="lucide:chevrons-up-down"
            className={`size-3.5 text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {isOpen && filtered.length > 0 && createPortal(
        <ul
          ref={listRef}
          role="listbox"
          style={dropdownStyle}
          className="z-[9999] max-h-60 p-1 bg-surface border border-border-default rounded-lg shadow-lg overflow-hidden overflow-y-auto"
        >
          {filtered.map((opt, i) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`
                cursor-pointer py-2 px-3 w-full text-sm rounded-lg
                flex justify-between items-center gap-2
                ${i === highlightIndex ? 'bg-hover-muted' : ''}
                ${opt.value === value ? 'text-brand font-medium' : 'text-foreground'}
                hover:bg-hover-muted
              `}
              onMouseEnter={() => setHighlightIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                selectOption(opt);
              }}
            >
              <span className="truncate">{opt.label}</span>
              {opt.value === value && (
                <Icon icon="lucide:check" className="size-3.5 text-brand shrink-0" />
              )}
            </li>
          ))}
        </ul>,
        document.body,
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

ComboBox.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
    ]),
  ),
  placeholder: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};
